// Regular month table (current + next pay period). Renders the desktop table
// and delegates to MobileBillCard on <640px screens.
import {
  Plus, Trash2, Copy, Zap, ExternalLink, CalendarClock, StickyNote, SplitSquareHorizontal,
} from "lucide-react";
import { formatMoney } from "@/lib/api";
import { displayFor } from "@/lib/bills-util";
import { Th, Td, EditableAmount, StatusDropdown } from "./table-cells";
import { shortDate, payPeriodClassFor } from "./utils";
import { MobileBillCard } from "./MobileBillCard";

// Plus is imported for future use / type safety; suppress unused-var lint via _.
void Plus;

export function MonthTable({ label, bills, today, ranges, onEdit, onReschedule, onSplit, onDelete, onDup, onStatus, onAmount, testId, emptyText, selectedIds = [], onToggleSelect }) {
  const totals = bills.reduce((acc, b) => {
    const d = displayFor(b, today);
    if (d.col === "paid") acc.paid += Number(b.estimated_due_amount);
    else acc.unpaid += Number(b.estimated_due_amount);
    return acc;
  }, { paid: 0, unpaid: 0 });

  return (
    <section data-testid={testId}>
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <h2 className="font-heading text-xl font-medium text-[#2C302E]">{label}</h2>
        {bills.length > 0 && (
          <div className="text-sm text-[#59605C]">
            Paid <span className="font-semibold text-[#3B5B3E]">{formatMoney(totals.paid)}</span>
            {" · "}
            Unpaid <span className="font-semibold text-[#8C5D30]">{formatMoney(totals.unpaid)}</span>
          </div>
        )}
      </div>

      {bills.length === 0 ? (
        <div className="card-soft p-8 text-center text-sm text-[#59605C]">{emptyText}</div>
      ) : (
        <div className="card-soft p-3 sm:!p-0 overflow-hidden">
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-sm" data-testid={`${testId}-table`}>
              <thead className="bg-[#F2EFEB] text-[#59605C] sticky top-0 z-10 shadow-[0_1px_0_#EBE6E0]">
                <tr>
                  <Th className="w-10">
                    {onToggleSelect && bills.length > 0 && (
                      <input
                        type="checkbox"
                        aria-label="Select all bills"
                        checked={bills.length > 0 && bills.every((b) => selectedIds.includes(b.id))}
                        onChange={(e) => {
                          if (e.target.checked) {
                            const allIds = bills.map((b) => b.id);
                            allIds.forEach((id) => { if (!selectedIds.includes(id)) onToggleSelect(id); });
                          } else {
                            bills.forEach((b) => { if (selectedIds.includes(b.id)) onToggleSelect(b.id); });
                          }
                        }}
                        data-testid={`${testId}-select-all`}
                      />
                    )}
                  </Th>
                  <Th className="w-24">Due date</Th>
                  <Th>Bill</Th>
                  <Th className="text-right w-32">Unpaid</Th>
                  <Th className="text-right w-32">Paid</Th>
                  <Th className="w-32">Status</Th>
                  <Th className="w-24">Recurring</Th>
                  <Th>Notes</Th>
                  <Th className="w-40 text-right">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {bills.map((b) => {
                  const d = displayFor(b, today);
                  const periodClass = payPeriodClassFor(b, ranges);
                  return (
                    <tr
                      key={b.id}
                      className={`border-t-2 border-[#D8CFB9] transition-colors ${periodClass} ${d.notCleared ? "!bg-[#FDF2F0]/40" : ""} ${selectedIds.includes(b.id) ? "!bg-[#EEF3EC]" : ""}`}
                      data-testid={`bill-row-${b.id}`}
                    >
                      <Td className="w-10">
                        {onToggleSelect && (
                          <input
                            type="checkbox"
                            aria-label={`Select bill ${b.name}`}
                            checked={selectedIds.includes(b.id)}
                            onChange={() => onToggleSelect(b.id)}
                            data-testid={`bill-select-${b.id}`}
                          />
                        )}
                      </Td>
                      <Td className="whitespace-nowrap font-medium text-[#2C302E]">
                        <button
                          type="button"
                          className="text-left hover:text-[#4A6148] hover:underline decoration-dotted underline-offset-2 focus:outline-none"
                          onClick={() => onReschedule(b)}
                          title="Click to reschedule this bill (this instance only)"
                          data-testid={`bill-due-date-${b.id}`}
                        >
                          {shortDate(b.due_date)}
                        </button>
                      </Td>
                      <Td>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {b.is_autopay && (
                            <span
                              className="inline-block w-2 h-2 rounded-full bg-[#3B5B3E] shrink-0"
                              aria-hidden="true"
                              title="Autopay — no action needed"
                              data-testid={`bill-autopay-dot-${b.id}`}
                            />
                          )}
                          <span className="text-[#2C302E] font-medium">{b.name}</span>
                          {b.notes && (
                            <span
                              className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#FEF3C7] text-[#7A5A1F]"
                              title={b.notes}
                              data-testid={`bill-note-badge-${b.id}`}
                            >
                              <StickyNote size={11} />
                            </span>
                          )}
                          {b.pay_url && (
                            <a
                              href={b.pay_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#EEF3EC] text-[#3B5B3E] hover:bg-[#D5E1D3]"
                              title="Open the online bill portal"
                              data-testid={`bill-pay-online-${b.id}`}
                            >
                              <ExternalLink size={9} /> pay online
                            </a>
                          )}
                          {b.is_autopay && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#EEF3EC] text-[#3B5B3E]" data-testid={`bill-autopay-${b.id}`}>
                              <Zap size={9} /> autopay
                            </span>
                          )}
                          {b.original_due_date && (
                            <span
                              className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#EDE7F6] text-[#4A3B7A]"
                              title={`Originally due ${b.original_due_date}`}
                              data-testid={`bill-rescheduled-pill-${b.id}`}
                            >
                              <CalendarClock size={9} /> rescheduled
                            </span>
                          )}
                        </div>
                      </Td>
                      <Td className="text-right" data-testid={`bill-unpaid-cell-${b.id}`}>
                        {d.col === "unpaid" ? (
                          <EditableAmount bill={b} onSave={onAmount} accent="unpaid" testId={`bill-amount-unpaid-${b.id}`} />
                        ) : (
                          <span className="text-[#EBE6E0]">—</span>
                        )}
                      </Td>
                      <Td className="text-right" data-testid={`bill-paid-cell-${b.id}`}>
                        {d.col === "paid" ? (
                          <EditableAmount bill={b} onSave={onAmount} accent={d.notCleared ? "not-cleared" : "paid"} testId={`bill-amount-paid-${b.id}`} />
                        ) : (
                          <span className="text-[#EBE6E0]">—</span>
                        )}
                      </Td>
                      <Td data-testid={`bill-status-cell-${b.id}`}>
                        <StatusDropdown bill={b} onChange={onStatus} />
                      </Td>
                      <Td className="text-[#59605C] text-xs">
                        {b.is_recurring ? b.recurrence_frequency : "One-time"}
                      </Td>
                      <Td className="text-[#59605C] text-xs max-w-[200px] truncate" title={b.notes || ""}>
                        {b.notes || "—"}
                      </Td>
                      <Td className="text-right">
                        <div className="inline-flex items-center gap-1 justify-end">
                          <button className="btn-ghost !px-2 !py-1 text-xs" onClick={() => onEdit(b)} data-testid={`bill-edit-${b.id}`}>
                            Edit
                          </button>
                          <button
                            className="btn-ghost !px-2 !py-1"
                            onClick={() => onReschedule(b)}
                            aria-label="Reschedule this instance"
                            title="Reschedule this instance (doesn't change the recurring day)"
                            data-testid={`bill-reschedule-${b.id}`}
                          >
                            <CalendarClock size={14} />
                          </button>
                          {onSplit && d.col === "unpaid" && Number(b.estimated_due_amount) > 0 && (
                            <button
                              className="btn-ghost !px-2 !py-1"
                              onClick={() => onSplit(b)}
                              aria-label="Split into a payment plan"
                              title="Split this bill into two halves across pay periods"
                              data-testid={`bill-split-${b.id}`}
                            >
                              <SplitSquareHorizontal size={14} />
                            </button>
                          )}
                          <button className="btn-ghost !px-2 !py-1" onClick={() => onDup(b)} aria-label="Duplicate" data-testid={`bill-dup-${b.id}`}>
                            <Copy size={14} />
                          </button>
                          <button className="btn-ghost !px-2 !py-1" onClick={() => onDelete(b)} aria-label="Delete" data-testid={`bill-del-${b.id}`}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Mobile cards */}
          <div className="sm:hidden space-y-2" data-testid={`${testId}-mobile-list`}>
            {bills.map((b) => (
              <MobileBillCard key={b.id} bill={b} today={today} ranges={ranges}
                onEdit={onEdit} onReschedule={onReschedule} onDelete={onDelete} onDup={onDup} onStatus={onStatus} onAmount={onAmount} />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
